import type {ReactElement} from 'react';
import {useCallback, useContext, useSyncExternalStore} from 'react';
import type {Editor} from 'grapesjs';
import {formMakerContext} from '@FormMakerReact/form-maker.context';
import {isDescriptionEnabled, toggleDescriptionEnabled} from '@FormMakerReact/lib/form-maker-fields-mutator-library';
import {ToggleSwitch, ToggleSwitchV2Size} from '@Components/toggle-switch-v2/toggle-switch';
import {Text, TextSize} from '@Components/text/text';
import styles from './description-toggle-control.module.scss';

const DESCRIPTION_SYNC_EVENTS = 'component:update:style component:add component:remove undo redo';

export function DescriptionToggleRow(): ReactElement {
  const {editor} = useContext(formMakerContext);
  const isEnabled = useDescriptionEnabledSync(editor);

  const handleToggle = (): void => {
    if (!editor) {
      return;
    }

    toggleDescriptionEnabled(editor);
  };

  return (
    <div className={styles.row}>
      <Text val={window.i18next.t('pmwjs_form_maker_description')} size={TextSize.MD} />
      <ToggleSwitch
        id="form-maker-description-toggle"
        isChecked={isEnabled}
        onChange={handleToggle}
        elementTitle={window.i18next.t('pmwjs_form_maker_description')}
        size={ToggleSwitchV2Size.LARGE}
      />
    </div>
  );
}

function useDescriptionEnabledSync(editor: Editor | null): boolean {
  const subscribe = useCallback(
    (onChange: () => void): (() => void) => {
      if (!editor) {
        return (): void => undefined;
      }

      editor.on(DESCRIPTION_SYNC_EVENTS, onChange);

      return (): void => {
        editor.off(DESCRIPTION_SYNC_EVENTS, onChange);
      };
    },
    [editor]
  );

  const getSnapshot = useCallback((): boolean => {
    return editor ? isDescriptionEnabled(editor) : false;
  }, [editor]);

  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}
